/**
 * Overlay rendered on top of a regular viewport when its series is a
 * DICOM Encapsulated PDF (SOP Class 1.2.840.10008.5.1.4.1.1.104.1).
 * Uses the browser's built-in PDF viewer via <iframe>, which Chrome,
 * Edge, Firefox and Safari (desktop + iPad) all support natively.
 *
 * The Cornerstone-managed viewport sits underneath but is intentionally
 * empty (we don't call setStack for PDF series). This keeps the tool
 * group registration intact so neighbouring panes still work normally.
 */
import { useEffect, useRef, useState } from 'react';
import styles from './PdfViewport.module.css';

interface Props {
  /** Caption shown in the top-left — typically patient + series description */
  label?: string;
  /** Stable identifier for what's being loaded — when this changes, the
   *  fetch re-runs. Typically `${studyId}/${seriesId}`. */
  cacheKey: string;
  /** Async loader for the PDF blob URL. Captured by ref so callers can
   *  pass an inline arrow function without triggering re-fetches on
   *  every parent render. */
  load: () => Promise<string>;
}

export default function PdfViewport({ label, cacheKey, load }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let cancelled = false;
    let currentUrl: string | null = null;
    setUrl(null);
    setError('');
    loadRef.current()
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        currentUrl = u;
        setUrl(u);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'Unable to load PDF.');
      });
    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [cacheKey]);

  function download() {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${Date.now()}.pdf`;
    a.click();
  }

  return (
    <div className={styles.wrap}>
      {label && <div className={styles.label}>{label}</div>}
      {url && (
        <button
          className={styles.downloadBtn}
          onClick={download}
          title="Download PDF"
        >
          ⬇ Download
        </button>
      )}
      {error ? (
        <div className={styles.error}>
          <div className={styles.errorTitle}>Couldn't display PDF</div>
          <div className={styles.errorDetail}>{error}</div>
        </div>
      ) : url ? (
        <iframe
          src={url}
          className={styles.frame}
          title="DICOM Encapsulated PDF"
        />
      ) : (
        <div className={styles.loading}>Loading PDF…</div>
      )}
    </div>
  );
}
